import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function AuthWrapper({ children }) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showUpdateCodeModal, setShowUpdateCodeModal] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [codeSuccess, setCodeSuccess] = useState(false);
  const [isUpdatingCode, setIsUpdatingCode] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const authData = sessionStorage.getItem('user_auth');
      if (authData) {
        const { timestamp, user } = JSON.parse(authData);
        // Check if 4 hours have passed
        if (Date.now() - timestamp < 4 * 60 * 60 * 1000) {
          // Still valid, set user data
          setUserData(user);
          setLoading(false);
          return;
        } else {
          // Expired, remove from session
          sessionStorage.removeItem('user_auth');
        }
      }
      // Not authenticated or expired, redirect to login
      sessionStorage.removeItem('user_auth');
      navigate('/login');
    };

    checkAuth();
  }, [navigate]);

  // Timer effect
  useEffect(() => {
    if (!userData) return;

    const updateTimer = () => {
      const authData = sessionStorage.getItem('user_auth');
      if (authData) {
        const { timestamp } = JSON.parse(authData);
        const timeRemaining = 4 * 60 * 60 * 1000 - (Date.now() - timestamp);
        if (timeRemaining > 0) {
          const minutes = Math.floor(timeRemaining / 60000);
          const seconds = Math.floor((timeRemaining % 60000) / 1000);
          setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          handleLogout();
        }
      }
    };

    updateTimer();
    const timerInterval = setInterval(updateTimer, 1000);

    return () => clearInterval(timerInterval);
  }, [userData]);



  const handleLogout = () => {
    sessionStorage.removeItem('user_auth');
    setUserData(null);
    navigate('/');
  };

  const canUpdateCode = userData?.role === 'admin' || userData?.role === 'instructor';

  const handleOpenUpdateCode = () => {
    setShowUpdateCodeModal(true);
    setNewCode('');
    setConfirmCode('');
    setCodeError(null);
    setCodeSuccess(false);
  };

  const handleUpdateCode = async (e) => {
    e.preventDefault();
    setCodeError(null);
    const trimmed = (newCode || '').trim().toLowerCase();
    const confirmTrimmed = (confirmCode || '').trim().toLowerCase();
    if (!trimmed) {
      setCodeError('Enter a new code.');
      return;
    }
    if (trimmed !== confirmTrimmed) {
      setCodeError('New code and confirmation do not match.');
      return;
    }
    setIsUpdatingCode(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ code: trimmed })
        .eq('id', userData.id);

      if (error) throw error;

      const updatedUser = { ...userData, code: trimmed };
      setUserData(updatedUser);
      const authData = sessionStorage.getItem('user_auth');
      if (authData) {
        const { timestamp } = JSON.parse(authData);
        sessionStorage.setItem('user_auth', JSON.stringify({ timestamp, user: updatedUser }));
      }
      setCodeSuccess(true);
      setNewCode('');
      setConfirmCode('');
      setTimeout(() => {
        setShowUpdateCodeModal(false);
        setCodeSuccess(false);
      }, 1500);
    } catch (err) {
      setCodeError(err.message || 'Failed to update code.');
    } finally {
      setIsUpdatingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="flex flex-col items-center gap-6 animate-fade-in">
          <div className="w-16 h-16 loading-spinner"></div>
          <div className="text-center">
            <p className="text-primary-600 font-semibold text-lg">Loading...</p>
            <p className="text-neutral-500 text-sm mt-1">Please wait while we set things up</p>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, don't render children (redirect will happen)
  if (!userData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-neutral-200 sticky top-0 z-40 safe-top">
        <div className="container-mobile py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.jpeg" 
                alt="Abubakar Islamic Institute Logo" 
                className="h-10 w-auto object-contain"
              />
              <div className="hidden sm:block text-left">
                <p className="font-semibold text-neutral-900">{userData?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {timeLeft && (
                <div className="hidden md:flex items-center text-sm text-neutral-500">
                  <div className="w-2 h-2 bg-warning-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="font-medium text-warning-600">{timeLeft}</span>
                </div>
              )}
              {canUpdateCode && (
                <button
                  type="button"
                  onClick={handleOpenUpdateCode}
                  className="p-2 rounded-lg text-neutral-500 hover:text-primary-600 hover:bg-primary-50"
                  title="Update my code"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="btn-ghost text-sm px-3 py-2"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content with mobile padding */}
      <main className="safe-bottom pb-20">
        {children}
      </main>

      {/* Update my code modal (admin / teacher) */}
      {showUpdateCodeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isUpdatingCode && setShowUpdateCodeModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-neutral-900">Update my access code</h3>
              <button
                type="button"
                onClick={() => !isUpdatingCode && setShowUpdateCodeModal(false)}
                className="p-2 rounded-lg text-neutral-400 hover:bg-neutral-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {codeSuccess ? (
              <p className="text-green-600 font-medium">Code updated. Use your new code next time you sign in.</p>
            ) : (
              <form onSubmit={handleUpdateCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">New code</label>
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    className="input w-full"
                    placeholder="Enter new code"
                    autoComplete="off"
                    disabled={isUpdatingCode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Confirm new code</label>
                  <input
                    type="text"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value)}
                    className="input w-full"
                    placeholder="Confirm new code"
                    autoComplete="off"
                    disabled={isUpdatingCode}
                  />
                </div>
                {codeError && (
                  <p className="text-sm text-red-600">{codeError}</p>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowUpdateCodeModal(false)}
                    className="btn-secondary flex-1"
                    disabled={isUpdatingCode}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                    disabled={isUpdatingCode}
                  >
                    {isUpdatingCode ? 'Updating...' : 'Update code'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
    </div>
  );
}
